// Sprint 6 (Phase 3, D49) — Render-time turn summary synthesis.
//
// `TurnSummaryCard` is purely client-side and never persisted. After every
// completed assistant turn (delimited by a `system_event` with
// `subtype: 'result'`), this pure function returns a structured summary of:
//   • files modified (Edit/Write/MultiEdit `file_path`s)
//   • verifications (typecheck, tests, lint, build) inferred from Bash
//     headlines and tool_result text
//   • system notes (model switches, plan-mode toggles, rate-limit pauses)
//   • result status (success / error / stopped)
//
// Suppression rules (the card is hidden when):
//   • the turn had zero tool calls (pure conversational reply), OR
//   • the turn was interrupted (no `result` event reached us yet), OR
//   • the turn errored before any tool ran (we have a result but
//     hasToolCalls=false → suppression collapses both signals).
//
// Verification regexes are deliberately loose because Bash invocations vary
// (`npx vitest`, `npm test --silent`, etc.). Misclassification is acceptable;
// false-positives at the headline level are handled by the heuristic
// returning the safest label per category.

import type { Message } from '../../types';
import { asToolUse } from '../messages';
import { longestCommonDir } from './path-utils';

export type TurnStatus = 'success' | 'error' | 'stopped';
export type VerificationKind = 'typecheck' | 'tests' | 'lint' | 'build';

export interface TurnVerification {
  kind: VerificationKind;
  passed: boolean;
  /** Short metric, e.g. "47/47", "3 failed", "0 errors". null when unknown. */
  metric: string | null;
  /** Raw bash command (developer-mode reveal). */
  rawCommand: string | null;
}

export interface TurnFilesModified {
  count: number;
  commonDir: string | null;
  /** All distinct paths in order seen, capped at 50 for bounded memory. */
  paths: string[];
}

export interface TurnSummary {
  /** id of the system_event `result` that closed this turn. */
  resultMessageId: string;
  status: TurnStatus;
  filesModified: TurnFilesModified | null;
  verifications: TurnVerification[];
  systemNotes: string[];
  hasToolCalls: boolean;
}

export interface SynthesizeInput {
  messages: Message[];
}

export function synthesizeTurnSummaries(
  messages: Message[],
): Map<string, TurnSummary> {
  const out = new Map<string, TurnSummary>();
  let buffer: Message[] = [];
  for (const m of messages) {
    if (m.type === 'system' && isResult(m)) {
      const summary = summarize(m, buffer);
      if (summary) out.set(m.id, summary);
      buffer = [];
      continue;
    }
    buffer.push(m);
  }
  return out;
}

function isResult(m: Message): boolean {
  const c = m.content as { subtype?: unknown };
  return !!c && c.subtype === 'result';
}

function summarize(resultMsg: Message, turn: Message[]): TurnSummary | null {
  const c = resultMsg.content as { result_subtype?: unknown };
  const status: TurnStatus = (() => {
    const sub = typeof c?.result_subtype === 'string' ? c.result_subtype : '';
    if (sub.includes('error')) return 'error';
    if (sub === 'success') return 'success';
    return 'stopped';
  })();

  // Pair tool_use with its tool_result inside this turn so the
  // verification heuristic can read both sides.
  const resultByToolUseId = new Map<string, Message>();
  for (const m of turn) {
    if (m.type === 'tool_result') {
      const tr = m.content as { tool_use_id?: unknown };
      const id = typeof tr?.tool_use_id === 'string' ? tr.tool_use_id : null;
      if (id) resultByToolUseId.set(id, m);
    }
  }

  const filePaths: string[] = [];
  const verifications: TurnVerification[] = [];
  const systemNotes: string[] = [];
  let hasToolCalls = false;

  for (const m of turn) {
    if (m.type === 'tool_use') {
      hasToolCalls = true;
      const tu = asToolUse(m);
      collectFiles(tu, filePaths);
      const v = detectVerification(tu, resultByToolUseId.get(tu.tool_use_id));
      if (v) verifications.push(v);
      continue;
    }
    if (m.type === 'system') {
      collectSystemNote(m, systemNotes);
    }
  }

  if (!hasToolCalls) return null;

  const filesModified = filePaths.length > 0
    ? {
        count: filePaths.length,
        commonDir: longestCommonDir(filePaths),
        paths: filePaths.slice(0, 50),
      }
    : null;

  return {
    resultMessageId: resultMsg.id,
    status,
    filesModified,
    verifications,
    systemNotes,
    hasToolCalls,
  };
}

const WRITE_TOOLS = new Set(['Edit', 'MultiEdit', 'Write']);

function collectFiles(
  tu: { name: string; input: Record<string, unknown> },
  out: string[],
) {
  if (!WRITE_TOOLS.has(tu.name)) return;
  const fp = tu.input.file_path;
  if (typeof fp !== 'string' || fp.length === 0) return;
  if (!out.includes(fp)) out.push(fp);
}

interface DetectedKind {
  kind: VerificationKind;
}

function classifyBash(cmd: string): DetectedKind | null {
  const c = cmd.toLowerCase();
  if (c.includes('npm run typecheck') || /\btsc\b/.test(c)) return { kind: 'typecheck' };
  if (
    c.includes('npm test') ||
    c.includes('npm run test') ||
    /\bvitest\b/.test(c) ||
    /\bjest\b/.test(c) ||
    /\bplaywright\b/.test(c)
  ) {
    return { kind: 'tests' };
  }
  if (c.includes('npm run lint') || /\beslint\b/.test(c)) return { kind: 'lint' };
  if (c.includes('npm run build')) return { kind: 'build' };
  return null;
}

function detectVerification(
  tu: { name: string; input: Record<string, unknown>; tool_use_id: string },
  result: Message | undefined,
): TurnVerification | null {
  if (tu.name !== 'Bash') return null;
  const cmd = typeof tu.input.command === 'string' ? tu.input.command : '';
  const detected = classifyBash(cmd);
  if (!detected) return null;
  const tr = result?.content as { is_error?: unknown; content?: unknown } | null;
  const text = (() => {
    if (!tr) return '';
    const inner = tr.content;
    if (typeof inner === 'string') return inner;
    if (Array.isArray(inner)) {
      return inner
        .map((p) => {
          if (p && typeof p === 'object' && typeof (p as { text?: unknown }).text === 'string') {
            return (p as { text: string }).text;
          }
          return '';
        })
        .join('\n');
    }
    return '';
  })();
  const passed = (() => {
    if (tr?.is_error === true) return false;
    if (!result) return false; // still running → can't claim passed yet
    // Empty text + no error → assume pass.
    if (text.length === 0) return true;
    return !looksFailed(text, detected.kind);
  })();
  const metric = parseMetric(detected.kind, text);
  return {
    kind: detected.kind,
    passed,
    metric,
    rawCommand: cmd || null,
  };
}

function looksFailed(text: string, kind: VerificationKind): boolean {
  const t = text.toLowerCase();
  if (kind === 'typecheck' || kind === 'build') {
    return /\berror ts\d+/i.test(text) || /\d+\s+errors?\b/i.test(text);
  }
  if (kind === 'tests') {
    return (
      /\b\d+\s+failed\b/.test(t) ||
      /\bfailing\b/.test(t) ||
      /\bfail(?:ed)?\b/.test(t)
    );
  }
  if (kind === 'lint') {
    return /\b\d+\s+errors?\b/.test(t) || /\bproblems?\b/.test(t);
  }
  return false;
}

function parseMetric(kind: VerificationKind, text: string): string | null {
  if (kind === 'tests') {
    const passed = /(\d+)\s+passed/i.exec(text);
    const failed = /(\d+)\s+failed/i.exec(text);
    const total = /Tests\s+(\d+)\s+passed\s+\((\d+)\)/i.exec(text);
    if (failed) return `${failed[1]} failed`;
    if (total) return `${total[1]}/${total[2]}`;
    if (passed) return `${passed[1]} passed`;
    return null;
  }
  if (kind === 'typecheck' || kind === 'build') {
    const errors = /(\d+)\s+errors?/i.exec(text);
    if (errors) return `${errors[1]} error${errors[1] === '1' ? '' : 's'}`;
    return null;
  }
  if (kind === 'lint') {
    const errors = /(\d+)\s+errors?/i.exec(text);
    const warns = /(\d+)\s+warning/i.exec(text);
    if (errors) return `${errors[1]} error${errors[1] === '1' ? '' : 's'}`;
    if (warns) return `${warns[1]} warning${warns[1] === '1' ? '' : 's'}`;
    return null;
  }
  return null;
}

function collectSystemNote(m: Message, notes: string[]) {
  const c = m.content as { subtype?: unknown };
  const sub = typeof c?.subtype === 'string' ? c.subtype : '';
  if (sub === 'config_changed') {
    const cc = m.content as {
      to?: { model?: unknown; effort?: unknown };
    };
    const model = typeof cc.to?.model === 'string' ? cc.to.model : null;
    if (model) notes.push(`Switched to ${humanModel(model)} mid-turn`);
    return;
  }
  // Plan-mode entries / rate-limit pauses are surfaced through other
  // subtypes the backend may add — keep this lookup permissive.
  if (sub === 'plan_mode_enter') notes.push('Entered plan mode');
  if (sub === 'plan_mode_exit') notes.push('Exited plan mode');
  if (sub === 'rate_limit_pause') notes.push('Paused on weekly cap');
}

function humanModel(model: string): string {
  if (model.includes('opus')) return 'Opus 4.6';
  if (model.includes('sonnet')) return 'Sonnet 4.5';
  if (model.includes('haiku')) return 'Haiku';
  return model;
}

